import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_TAGS, createLazyQuery } from '@/shared/api';
import { clearUserUrlLog, createUserUrlLog, fetchUserUrlLog } from './api';
import type { UserUrlLogListParams } from './types';

/**
 * Query keys лога URL пользователей.
 */
export const userUrlLogKeys = {
  all: [API_TAGS.USER_URL_LOGS] as const,
  lists: () => [...userUrlLogKeys.all, 'list'] as const,
  list: (params?: UserUrlLogListParams) => [...userUrlLogKeys.lists(), { ...params }] as const,
};

/**
 * Lazy-хук получения порции записей лога URL для бесконечной прокрутки (infinite scroll).
 * Запрос запускается вручную через `refetch(params)` из `fetchPage` DataGrid (lazy-режим):
 * каждая порция тянется сдвигом `offset` при фиксированном `limit`. Порции с разными
 * offset/sort/фильтрами кэшируются React Query по ключу; одинаковые параметры берутся из кэша.
 */
export const useLazyUserUrlLog = createLazyQuery(
  (params: UserUrlLogListParams) => userUrlLogKeys.list(params),
  fetchUserUrlLog,
);

/**
 * Хук fire-and-forget записи перехода пользователя на URL.
 * Ошибки намеренно не обрабатываются: сбой логирования не должен влиять на работу приложения.
 */
export const useLogUserUrl = () => {
  return useMutation({
    mutationFn: createUserUrlLog,
  });
};

/**
 * Хук очистки лога URL по периоду (удаляет записи старше указанного момента).
 * По успеху удаляет кэш списков целиком, а не инвалидирует: lazy-запрос порций тянется
 * через fetchQuery, который оценивает свежесть только по времени (isStaleByTime) и
 * игнорирует флаг инвалидации, — повторный запрос той же порции после очистки вернул бы
 * устаревший кэш. Удаление кэша гарантирует свежий запрос из сети при сбросе таблицы к первой порции.
 */
export const useClearUserUrlLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearUserUrlLog,
    onSuccess: () => queryClient.removeQueries({ queryKey: userUrlLogKeys.lists() }),
  });
};
